import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ApplyApi, IApplicationData, IApplicationInformation, ISaveApplicationResponse } from '@apply/api/src/api/ApplyApi';
import { applicationInsights } from '../ApplicationInsights';

const applyApi = new ApplyApi(applicationInsights);

export enum ApplicationQueryKeys {
    Application = 'application',
}

export function applicationQueryKey(entryGuid: string) {
    return [ApplicationQueryKeys.Application, entryGuid] as const;
}

export function useApplicationQuery(entryGuid: string | undefined, token: string | null = null) {
    return useQuery({
        queryKey: applicationQueryKey(entryGuid ?? ''),
        queryFn: async (): Promise<IApplicationData> => {
            const response = await applyApi.getApplication(entryGuid!, token);
            if (response.statusCode !== 200) {
                throw new Error('Get Application call failed.');
            }
            return response.result;
        },
        enabled: !!entryGuid,
        staleTime: Infinity,
    });
}

interface SaveApplicationArgs {
    entryGuid: string;
    email: string;
    information: IApplicationInformation;
    complete: boolean;
    pausePoint: string | null;
}

export function useSaveApplicationMutation() {
    const queryClient = useQueryClient();
    return useMutation<ISaveApplicationResponse, Error, SaveApplicationArgs>({
        mutationFn: async ({ entryGuid, email, information, complete, pausePoint }) => {
            const data: IApplicationData = {
                entryGuid,
                email,
                information: JSON.stringify(information) as unknown as IApplicationInformation,
                complete,
                pausePoint,
            };
            const response = await applyApi.saveApplication(data);
            if (response.statusCode !== 200) throw new Error('Save Application failed');
            return response.result;
        },
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: applicationQueryKey(variables.entryGuid) });
        },
    });
}

export function useApplicationData(entryGuid: string | undefined): IApplicationData | undefined {
    const queryClient = useQueryClient();
    if (!entryGuid) return undefined;
    return queryClient.getQueryData<IApplicationData>(applicationQueryKey(entryGuid));
}

export function useClearApplicationCache() {
    const queryClient = useQueryClient();
    return (entryGuid: string = '') => {
        queryClient.removeQueries({ queryKey: applicationQueryKey(entryGuid) });
    };
}
